import { Card, Flex, notification, Switch, Table } from "antd"
import { defaultVisit, Visit, VisitReviewedState } from "../../domain/jobs/jobs"
import { useUpdateVisitMutation, useVisitsQuery } from "../../domain/jobs/jobsQueries"
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime';
import { ReactNode, useContext, useState } from "react";
import { ItemsContext } from "../Items/ItemsRoot";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentAlt, faTrash, faPencil, faImage } from "@fortawesome/pro-duotone-svg-icons";
import { faCheck, faCommentAlt as faCommentAltSolid, faImage as faImageSolid } from "@fortawesome/pro-solid-svg-icons";
import { centsToDollarsDisplay } from "../displayFormatters";
import VisitItems from "../Items/VisitItems/VisitItems";
import VisitDetailsModal from "./VisitDetailsModal";
import DeleteVisitModal from './DeleteVisitModal'
import { blue, red } from '@ant-design/colors'
import { SectionLayoutContext } from '../common/SectionCard'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import { JOB_DETAILS_CLEANUP_FEATURE } from '../../domain/jobs/jobs'

dayjs.extend(relativeTime);

type Props = {
  reviewedState: VisitReviewedState
  jobId: number
  googleApiKey: string
  onEdit: (visitId: string) => void
  toolbar?: ReactNode
}

const VisitsTable = ({reviewedState, jobId, googleApiKey, onEdit, toolbar} : Props) => {
  const updateMutation = useUpdateVisitMutation()
  const [isDeleteVisitModalOpen, setIsDeleteVisitModalOpen] = useState(false)
  const [isVisitDetailsModalOpen, setIsVisitDetailsModalOpen] = useState(false)
  const [selectedVisit, setSelectedVisit] = useState<Visit>(defaultVisit)
  const [activeTab, setActiveTab] = useState('1')
  const { visitSummary } = useContext(ItemsContext)
  const layout = useContext(SectionLayoutContext)
  const showPhotos = useFeatureFlag(JOB_DETAILS_CLEANUP_FEATURE)
  const getVisitsQuery = useVisitsQuery(jobId)
  const visits = getVisitsQuery.data ?? []
  const reviewedVisits = visits.filter((visit) => visit.billed === true) ?? []
  const notReviewedVisits = visits.filter((visit) => visit.billed === false) ?? []
  const notReviewedVisitSorted = notReviewedVisits.sort((a, b) => b.date.localeCompare(a.date))
  const reviewedVisitSorted = reviewedVisits.sort((a, b) => b.date.localeCompare(a.date))

  const isReviewed = reviewedState === VisitReviewedState.Reviewed

  const getVisitHours = (visit: Visit) => {
    if (visitSummary) {
      const summary = visitSummary.find(x => x.visitId === visit.id)
      if (summary) {
        return summary.hours
      }
    }
    return visit.laborTime || visit.time
  }

  const getVisitNotSure = (visit: Visit) => {
    if (!visit) {
      return;
    }

    if (visitSummary) {
      const summary = visitSummary.find(x => x.visitId === visit.id)
      if (summary) {
        return summary.unsure
      }
    }
    return false
  }

  const getVisitActual = (visit: Visit) => {
    if (!visit) return;

    if (visitSummary) {
      const summary = visitSummary.find(x => x.visitId === visit.id)
      if (summary) {
        return summary.totalPrice
      }
    }
    return 0
  }

  const doesVisitHaveAnythingInvoiced = (visit: Visit) => {
    if (!visit) {
      return;
    }

    if (visitSummary) {
      const summary = visitSummary.find(x => x.visitId === visit.id)
      if (summary) {
        return summary.hasInvoicedItem
      }
    }

    return false
  }

  const handleDeleteVisit = (visit: Visit) => { 
    if(doesVisitHaveAnythingInvoiced(visit)) {
      notification.error({
        message: 'Cannot Delete Visit',
        description: 'This visit has invoiced items. Please remove them before deleting the visit.'
      })
      return
    }

    setIsDeleteVisitModalOpen(true)
  }

  const handleUpdateVisit = (visitId: string, billed: boolean, clientNotes: string, internalNotes: string, status: number) => {
  
    updateMutation.mutate({
      visitId,
      billed,
      clientNotes,
      internalNotes,
      status
    })
  
  }

  const handleVisitDetailClose = (updatedVisit: Visit) => {
    if(updatedVisit.status !== selectedVisit?.status || 
      updatedVisit.clientNotes !== selectedVisit?.clientNotes ||
      updatedVisit.internalNotes !== selectedVisit?.internalNotes
    ){
      updateMutation.mutate({
        visitId: updatedVisit.id,
        billed: updatedVisit.billed,
        clientNotes: updatedVisit.clientNotes,
        internalNotes: updatedVisit.internalNotes,
        status: updatedVisit.status
      })
    }
  }

  const content = <>
    {toolbar}
    <Table 
      dataSource={isReviewed ? reviewedVisitSorted : notReviewedVisitSorted}
      rowKey={(record) => record.id}
      columns={[
        {
          title: 'Date',
          dataIndex: 'date',
          key: 'date',
          render: (date) => <>
            {dayjs(date).format('MMMM DD, YYYY') ?? dayjs().format('MMMM DD, YYYY')}
            <br />
            <small>({dayjs(date).fromNow()})</small>
          </>
        },
        {
          title: 'Workarea',
          dataIndex: 'tasks',
          key: 'tasks'
        },
        {
          title: 'Technicians',
          render: (visit) => <a
              style={{color: '#428bca'}}
              onClick={() => {
                setSelectedVisit(visit)
                setActiveTab('1')
                setIsVisitDetailsModalOpen(true)
              }}
            >
              {visit.technicians}
            </a>
        },
        {
          title: 'Time',
          render: (visit) => getVisitHours(visit) + ' hr',
        },
        {
          title: 'Client Notes',
          render: (visit) => <Flex style={{justifyContent: 'center'}}>
            <a
              onClick={() => {
                setSelectedVisit(visit)
                setActiveTab('2')
                setIsVisitDetailsModalOpen(true)
              }}
            >
              <FontAwesomeIcon icon={ visit.clientNotes ? faCommentAltSolid : faCommentAlt} />
            </a>
          </Flex>
        },
        {
          title: 'Internal Notes',
          render: (visit) => <Flex style={{justifyContent: 'center'}}>
            <a
              onClick={() => {
                setSelectedVisit(visit)
                setActiveTab('3')
                setIsVisitDetailsModalOpen(true)
              }}
            >
              <FontAwesomeIcon icon={ visit.internalNotes ? faCommentAltSolid : faCommentAlt} />
            </a>
          </Flex>
        },
        {
          title: 'Photos',
          key: 'photos',
          hidden: !showPhotos,
          render: (visit) => <Flex style={{justifyContent: 'center'}}>
            <a
              onClick={() => {
                setSelectedVisit(visit)
                setActiveTab('4')
                setIsVisitDetailsModalOpen(true)
              }}
            >
              <FontAwesomeIcon icon={visit.hasPhotos ? faImageSolid : faImage} />
            </a>
          </Flex>
        },
        {
          title: 'Not sure',
          render: (visit) => getVisitNotSure(visit) ? <Flex style={{justifyContent: 'center'}}><FontAwesomeIcon icon={faCheck} /></Flex> : <></>,
          key: 'notSure',
          hidden: isReviewed ?? false
        },
        {
          title: 'Reviewed',
          render: (visit) => 
          <Switch 
            checked={visit.billed} 
            onChange={(value) => { 
              handleUpdateVisit(visit.id, value, visit.clientNotes, visit.internalNotes, visit.status)
            }} 
          />
        },
        {
          title: 'Submitted',
          render: (visit) => visit.status > 1 ? <Flex style={{justifyContent: 'center'}}><FontAwesomeIcon icon={faCheck} /></Flex> : <></> 
        },
        {
          title: 'Total Price',
          render: (visit) => centsToDollarsDisplay(getVisitActual(visit))
        },
        {
          render: (visit) => {
            if (visit.billed) {
              return <></>
            }
            return <Flex style={{justifyContent: 'center'}} gap={12}>
              <a onClick={() => {
                setSelectedVisit(visit)
                onEdit(visit.id)
              }}>
                <FontAwesomeIcon icon={faPencil} color={blue.primary} />
              </a>
              <a onClick={() => {
                setSelectedVisit(visit)
                handleDeleteVisit(visit)
              }}>
                <FontAwesomeIcon icon={faTrash} color={red.primary} />
              </a>
            </Flex>
          },
        }
      ]}
      expandable={{
        expandedRowRender: (record) => (
          <VisitItems
            visitId={record.id} 
          />
        ),
      }}
    />
    <VisitDetailsModal
      isOpen={isVisitDetailsModalOpen}
      visit={selectedVisit!}
      onClose={(detailsVisit) => {
        setIsVisitDetailsModalOpen(false)
        handleVisitDetailClose(detailsVisit)}}
      activeTab={activeTab}
      googleApiKey={googleApiKey}
      showPhotos={showPhotos}
      jobId={jobId}
    />
    <DeleteVisitModal
      visitId={selectedVisit.id}
      isOpen={isDeleteVisitModalOpen}
      onClose={() => setIsDeleteVisitModalOpen(false)}
    />
  </>

  if (layout === 'card') {
    return <Card title={`${isReviewed ? 'Reviewed' : 'Not-Reviewed'} Visits`}>
      {content}
    </Card>
  }

  return <section>
    <h4 style={{marginBottom: '10px'}}>
      <b>
        {isReviewed ? "Reviewed" : "Not-Reviewed"}
      </b> Visits
    </h4>
    {content}
  </section>
}

export default VisitsTable